import { useCallback, useMemo, useState } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useFornecedoresListaPagination } from "../hooks/Fornecedores_lista/pagination";
import { FornecedorLista } from "../../domain/models/fornecedor";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { initialFornecedoresListaFilters, useFornecedoresListaFilters } from "../hooks/Fornecedores_lista/filters";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { FornecedorFamiliaDropDown } from "../../domain/models/fornecedor_familia_dropdown";
import { FornecedorAtividadeDropDown } from "../../domain/models/fornecedor_atividade_dropdown";
import { CriterioAvaliacaoFornecedor } from "../../domain/models/criterio_avaliacao_fornecedor";
import { AvaliacaoFornecedorForm } from "../schemas/ProdutosServicos_lista/avaliacao_fornecedor";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

/**
 * LoadFornecedoresFuncState
 */

type LoadFornecedoresFuncState = {
  razaoSocial?: string;
  cnpj?: string;
  cpf?: string;
  nomeFantasia?: string;
  centroCustos?: number[] | null;
  familias?: string[] | null;
  atividades?: string[] | null;
  documento?: number;
  tipoFornecedor?: number;
  status?: number;
  avaliacaoCentroCusto?: number[] | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useFornecedoresListaViewModel
 */

export const useFornecedoresListaViewModel = () => {
  const {
    commons: {
      getFuncionarioByIdUsuarioUseCase,
    },
    reports: {
      getReportListaFornecedorUseCase,
    },
    compras: {
      getFornecedoresFamiliasDropDownUseCase,
      getFornecedoresAtividadesDropDownUseCase,
      getAllFornecedorUseCase,
      deleteFornecedorUseCase,
      getAllCriterioAvaliacaoFornecedorUseCase,
      createAvaliacaoFornecedorUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadFornecedoresDropDown,
  } = useDropDownLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedHandleRef = useCreateDebounceHandler(750);

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const { idUsuario } = useUserAuthStore();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionarioInformation, setFuncionarioInformation] = useState<FuncionarioInformation>();

  const [avaliacaoFornecedorModal, setAvaliacaoFornecedorModal] = useState(false);
  const [fornecedorDeleteModal, setFornecedorDeleteModal] = useState(false);

  const [familiasDropdown, setFamiliasDropdown] = useState<FornecedorFamiliaDropDown[]>();
  const [atividadesDropdown, setAtividadesDropdown] = useState<FornecedorAtividadeDropDown[]>();
  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [fornecedores, setFornecedores] = useState<FornecedorLista[]>();
  const [criteriosAvaliacaoFornecedor, setCriteriosAvaliacaoFornecedor] = useState<CriterioAvaliacaoFornecedor[]>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const [selectedFornecedor, setSelectedFornecedor] = useState<FornecedorLista>();

  const filters = useFornecedoresListaFilters();
  const pagination = useFornecedoresListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filteredFuncionariosDropdown = useMemo(() => {
    return funcionariosDropdown
      ? funcionariosDropdown.filter(x => x.canBeSelected === true)
      : null;
  }, [
    funcionariosDropdown
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadFornecedoresFamiliasDropdown = useCallback(async () => {
    try {
      const results = await getFornecedoresFamiliasDropDownUseCase
        .getFornecedoresFamiliasDropDown();
      setFamiliasDropdown(results);
    } catch (e: unknown) {
      setFamiliasDropdown(undefined);
      throw new Error('Falha ao buscar familias de fornecedores!');
    }
  }, [
    getFornecedoresFamiliasDropDownUseCase,
  ]);

  const loadFornecedoresAtividadesDropdown = useCallback(async () => {
    try {
      const results = await getFornecedoresAtividadesDropDownUseCase
        .getFornecedoresAtividadesDropDown();
      setAtividadesDropdown(results);
    } catch (e: unknown) {
      setAtividadesDropdown(undefined);
      throw new Error('Falha ao buscar atividades de fornecedores!');
    }
  }, [
    getFornecedoresAtividadesDropDownUseCase,
  ]);

  const loadFuncionarioByIdUsuario = useCallback(async () => {
    try {
      const results = await getFuncionarioByIdUsuarioUseCase
        .getFuncionarioByIdUsuario(idUsuario);
      setFuncionarioInformation(results ?? undefined);
    } catch (e: unknown) {
      setFuncionarioInformation(undefined);
      throw new Error('Falha ao buscar dados do funcionario!');
    }
  }, [
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
  ]);

  const loadCriteriosAvaliacaoFornecedor = useCallback(async () => {
    try {
      const results = await getAllCriterioAvaliacaoFornecedorUseCase
        .getAllCriterioAvaliacaoFornecedor();
      setCriteriosAvaliacaoFornecedor(results);
    } catch (e: unknown) {
      setCriteriosAvaliacaoFornecedor(undefined);
      throw new Error('Falha ao buscar criterios de avaliacao de fornecedor!');
    }
  }, [
    getAllCriterioAvaliacaoFornecedorUseCase,
  ]);

  const loadFornecedores = useCallback(async (fs?: LoadFornecedoresFuncState) => {
    try {
      const filteredRazaoSocial = evaluateParamValue(fs?.razaoSocial, filters.razaoSocial);
      const filteredCnpj = evaluateParamValue(fs?.cnpj, filters.cnpj);
      const filteredCpf = evaluateParamValue(fs?.cpf, filters.cpf);
      const filteredNomeFantasia = evaluateParamValue(fs?.nomeFantasia, filters.nomeFantasia);
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredFamilias = evaluateParamValue(fs?.familias, filters.familias);
      const filteredAtividades = evaluateParamValue(fs?.atividades, filters.atividades);
      const filteredDocumento = evaluateParamValue(fs?.documento, filters.documento);
      const filteredTipoFornecedor = evaluateParamValue(fs?.tipoFornecedor, filters.tipoFornecedor);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredAvaliacaoCentroCusto = evaluateParamValue(fs?.avaliacaoCentroCusto, filters.avaliacaoCentroCusto);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllFornecedorUseCase.getAllFornecedor(
        filteredRazaoSocial,
        filteredCnpj,
        filteredCpf,
        filteredNomeFantasia,
        filteredCentroCustos,
        filteredFamilias,
        filteredAtividades,
        filteredDocumento,
        filteredTipoFornecedor,
        filteredStatus,
        filteredAvaliacaoCentroCusto,
        filteredPage,
        filteredRowsPerPage,
      );

      setFornecedores(getAllFornecedorUseCase.items.map(x => ({
        idFornecedor: x.idFornecedor,
        dataCadastro: x.dataCadastro,
        nomeFantasia: x.nomeFantasia,
        razaoSocial: x.razaoSocial,
        notaMedia: x.notaMedia,
        familia: x.familia,
        ativo: x.ativo,
        statusDocumentos: x.statusDocumentos,
        desqualificado: x.desqualificado,
        nroFornecedor: x.nroFornecedor,
        fornProduto: x.fornProduto,
        fornServico: x.fornServico,
      })));

      pagination.setTotalItems(getAllFornecedorUseCase.totalItems);
    } catch (e: unknown) {
      setFornecedores(undefined);
      throw new Error('Falha ao buscar dados dos fornecedores!');
    }
  }, [
    filters,
    pagination,
    getAllFornecedorUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadFuncionarioByIdUsuario();

      await loadFornecedoresFamiliasDropdown();

      await loadFornecedoresAtividadesDropdown();

      await loadCriteriosAvaliacaoFornecedor();

      await loadFornecedores();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadFuncionarioByIdUsuario();

      await loadFornecedoresFamiliasDropdown();

      await loadFornecedoresAtividadesDropdown();

      await loadCriteriosAvaliacaoFornecedor();

      await loadFornecedores();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadFornecedoresDropDown,
    loadFuncionarioByIdUsuario,
    loadFornecedoresFamiliasDropdown,
    loadFornecedoresAtividadesDropdown,
    loadCriteriosAvaliacaoFornecedor,
    loadFornecedores,
    pushErrorNotification,
  ]);

  const handleAvaliacaoSubmit = useCallback(async (form: AvaliacaoFornecedorForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      await createAvaliacaoFornecedorUseCase.createAvaliacaoFornecedor(
        form.fornecedores,
        form.avaliadoPor,
        form.data,
        form.centroCusto,
        form.nroNotaFiscal,
        form.observacao,
        form.criteriosAvaliacaoFornecedor.map(x => ({
          idCriterioAvaliacaoFornecedor: x.idCriterioAvaliacaoFornecedor,
          nota: x.nota,
        })),
      );

      await loadFornecedores();

      setRowSelectionModel({ type: 'include', ids: new Set() });
      setAvaliacaoFornecedorModal(false);

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar avaliação de fornecedor!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    createAvaliacaoFornecedorUseCase,
    loadFornecedores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleDeleteSubmit = useCallback(async () => {
    if (selectedFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteFornecedorUseCase.deleteFornecedor(
          selectedFornecedor.idFornecedor,
        );

        await loadFornecedores();

        setFornecedorDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir fornecedor!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedFornecedor,
    deleteFornecedorUseCase,
    loadFornecedores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useFornecedoresListaPagination.setState({ page: page + 1 });
      await loadFornecedores({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFornecedores,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useFornecedoresListaPagination.setState({ page: 1, rowsPerPage });
      await loadFornecedores({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFornecedores,
    pushErrorNotification,
  ]);

  const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
    setRowSelectionModel(rows);
  }, [
  ]);

  const handleEditar = useCallback((idFornecedor: number | null, openNewTab: boolean) => {
    if (idFornecedor) {
      if (openNewTab) {
        window.open(`/Fornecedores_cadastro?id=${idFornecedor}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/Fornecedores_cadastro?id=${idFornecedor}`, { alternativeRoute: '/Fornecedores_lista' });
      }
    } else {
      if (openNewTab) {
        window.open(`/Fornecedores_cadastro`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/Fornecedores_cadastro`, { alternativeRoute: '/Fornecedores_lista' });
      }
    }

  }, [
    backOrNavigate,
  ]);

  const handleDeleteModal = useCallback((idFornecedor: number | null, openState: boolean) => {
    if (openState) {
      if (idFornecedor && fornecedores) {
        setSelectedFornecedor(fornecedores.find(x => x.idFornecedor === idFornecedor));
      } else {
        setSelectedFornecedor(undefined);
      }
    }
    setFornecedorDeleteModal(openState);
  }, [
    fornecedores,
  ]);

  const handleAvaliacaoModalOpen = useCallback((openState: boolean) => {
    setAvaliacaoFornecedorModal(openState);
  }, [
  ]);

  const handleRazaoSocialChange = useDebouncedCallback(debouncedHandleRef, (razaoSocial: string) => {
    useFornecedoresListaFilters.setState({ razaoSocial });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, razaoSocial });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleCnpjChange = useDebouncedCallback(debouncedHandleRef, (cnpj: string) => {
    useFornecedoresListaFilters.setState({ cnpj });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, cnpj });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleCpfChange = useDebouncedCallback(debouncedHandleRef, (cpf: string) => {
    useFornecedoresListaFilters.setState({ cpf });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, cpf });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleNomeFantasiaChange = useDebouncedCallback(debouncedHandleRef, (nomeFantasia: string) => {
    useFornecedoresListaFilters.setState({ nomeFantasia });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, nomeFantasia });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleCentroCustosChange = useDebouncedCallback(debouncedHandleRef, (centroCustos: number[] | null) => {
    useFornecedoresListaFilters.setState({ centroCustos });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, centroCustos });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleFamiliasChange = useDebouncedCallback(debouncedHandleRef, (familias: string[] | null) => {
    useFornecedoresListaFilters.setState({ familias });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, familias });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleAtividadesChange = useDebouncedCallback(debouncedHandleRef, (atividades: string[] | null) => {
    useFornecedoresListaFilters.setState({ atividades });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, atividades });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleDocumentoChange = useDebouncedCallback(debouncedHandleRef, (documento: number) => {
    useFornecedoresListaFilters.setState({ documento });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, documento });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleTipoFornecedorChange = useDebouncedCallback(debouncedHandleRef, (tipoFornecedor: number) => {
    useFornecedoresListaFilters.setState({ tipoFornecedor });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, tipoFornecedor });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandleRef, (status: number) => {
    useFornecedoresListaFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, status });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleAvaliacaoCentroCustoChange = useDebouncedCallback(debouncedHandleRef, (avaliacaoCentroCusto: number[] | null) => {
    useFornecedoresListaFilters.setState({ avaliacaoCentroCusto });
    return async () => {
      try {
        setLoading(true);
        useFornecedoresListaPagination.setState({ page: 1 });
        await loadFornecedores({ page: 1, avaliacaoCentroCusto });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
    loadFornecedores,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useFornecedoresListaFilters.setState({ ...initialFornecedoresListaFilters });
      useFornecedoresListaPagination.setState({ page: 1 });
      await loadFornecedores({ ...initialFornecedoresListaFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFornecedores,
    pushErrorNotification,
  ]);

  const handleReportListaFornecedores = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportListaFornecedorUseCase.getReportListaFornecedor(requiredMimeType),
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio de lista de fornecedores!'))
    } finally {
      setLoading(false);
    }
  }, [
    getReportListaFornecedorUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  return {
    idUsuario,
    loading,
    saving,
    errorMessage,
    funcionarioInformation: funcionarioInformation ?? null,
    avaliacaoFornecedorModal,
    fornecedorDeleteModal,
    familiasDropdown: familiasDropdown ?? [],
    atividadesDropdown: atividadesDropdown ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    fornecedores: fornecedores ?? [],
    criteriosAvaliacaoFornecedor: criteriosAvaliacaoFornecedor ?? [],
    rowSelectionModel,
    filters,
    pagination,
    reportViewerState,
    handleReload,
    handleAvaliacaoSubmit,
    handleDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowSelectionModelChange,
    handleEditar,
    handleDeleteModal,
    handleAvaliacaoModalOpen,
    handleRazaoSocialChange,
    handleCnpjChange,
    handleCpfChange,
    handleNomeFantasiaChange,
    handleCentroCustosChange,
    handleFamiliasChange,
    handleAtividadesChange,
    handleDocumentoChange,
    handleTipoFornecedorChange,
    handleStatusChange,
    handleAvaliacaoCentroCustoChange,
    handleClearFilters,
    handleReportListaFornecedores,
  };
}
